/**
 * HYROX-resolved semantic colors (HYROX mode, var-set 11-71).
 * https://www.figma.com/design/PWHx0qpMARnee0XAzaaGO2/GLOBAL-DESIGN-SYSTEM?node-id=0-1&m=dev&vars=1&var-set-id=11-71
 *
 * Source of truth: HYROX.tokens.json (Figma export, `com.figma.modeName`: HYROX).
 * Last synced: 2026-05-21
 * Do not copy hex from Figma MCP get_design_context fallbacks (often FIIT / first mode).
 *
 * Usage: `theme.semantic.accent_500` (Figma `semantic colors/accent/500`)
 */

export type SemanticColorStep = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

type SemanticColorScale = Record<SemanticColorStep, string>;

type FlatSemanticScale<Prefix extends string> = {
  [Step in SemanticColorStep as `${Prefix}_${Step}`]: string;
};

export type HyroxSemanticColors = FlatSemanticScale<'accent'> &
  FlatSemanticScale<'neutrals'> &
  FlatSemanticScale<'error'> &
  FlatSemanticScale<'success'> &
  FlatSemanticScale<'info'> &
  FlatSemanticScale<'warnning'> & {
    brand_900: string;
    brand_950: string;
    /** Figma `texts-small-elements` */
    text_small_elements: string;
    /** Figma `content-primary-button` */
    content_primary_button: string;
  };

const SEMANTIC_STEPS: SemanticColorStep[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

const flattenScale = <Prefix extends string>(prefix: Prefix, scale: SemanticColorScale): FlatSemanticScale<Prefix> =>
  Object.fromEntries(SEMANTIC_STEPS.map((step) => [`${prefix}_${step}`, scale[step]])) as FlatSemanticScale<Prefix>;

const neutralsScale: SemanticColorScale = {
  50: '#08080a',
  100: '#18181b',
  200: '#27272a',
  300: '#3f3f46',
  400: '#52525b',
  500: '#71717a',
  600: '#ababb2',
  700: '#c3c3ca',
  800: '#e5e5e7',
  900: '#f5f5f6',
  950: '#ffffff',
};

const accentScale: SemanticColorScale = {
  50: '#1a1800',
  100: '#403b00',
  200: '#554f02',
  300: '#aca001',
  400: '#dbcc02',
  500: '#ffed00',
  600: '#fff466',
  700: '#fff899',
  800: '#fffab0',
  900: '#fffccf',
  950: '#fffde7',
};

const errorScale: SemanticColorScale = {
  50: '#450a0a',
  100: '#7f1d1d',
  200: '#991b1b',
  300: '#b91c1c',
  400: '#dc2626',
  500: '#ef4444',
  600: '#f87171',
  700: '#fca5a5',
  800: '#fecaca',
  900: '#ffe2e2',
  950: '#fef2f2',
};

const successScale: SemanticColorScale = {
  50: '#052e16',
  100: '#14532d',
  200: '#166534',
  300: '#15803d',
  400: '#16a34a',
  500: '#22c55e',
  600: '#4ade80',
  700: '#86efac',
  800: '#bbf7d0',
  900: '#dcfce7',
  950: '#f0fdf4',
};

const infoScale: SemanticColorScale = {
  50: '#172554',
  100: '#1e3a8a',
  200: '#1e40af',
  300: '#1d4ed8',
  400: '#2563eb',
  500: '#3b82f6',
  600: '#60a5fa',
  700: '#93c5fd',
  800: '#bfdbfe',
  900: '#dbeafe',
  950: '#eff6ff',
};

/** Figma spelling `warnning` */
const warnningScale: SemanticColorScale = {
  50: '#422006',
  100: '#713f12',
  200: '#854d0e',
  300: '#a16207',
  400: '#ca8a04',
  500: '#eab308',
  600: '#facc15',
  700: '#fde047',
  800: '#fef08a',
  900: '#fef9c3',
  950: '#fefce8',
};

export const hyroxSemanticColors: HyroxSemanticColors = {
  ...flattenScale('accent', accentScale),
  ...flattenScale('neutrals', neutralsScale),
  ...flattenScale('error', errorScale),
  ...flattenScale('success', successScale),
  ...flattenScale('info', infoScale),
  ...flattenScale('warnning', warnningScale),
  brand_900: neutralsScale[100],
  brand_950: neutralsScale[50],
  /** Figma `texts-small-elements` (HYROX on 20648:8676) */
  text_small_elements: '#fafafa',
  /** Figma `content-primary-button` (HYROX on 20648:8677) */
  content_primary_button: '#08080a',
};
